import { useMemo, useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  RotateCcw,
  Send,
  Receipt,
  User2,
  MapPin,
  StickyNote,
  Package,
  Tag,
  Paperclip,
  X,
  Upload,
  UserCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

import { VARIETIES, PRICELISTS, SEGMENTS, CURRENCIES, PRICELIST_RATES, MANUAL_PRICELISTS } from "../data/catalog";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const makeOrderNo = () => {
  const d = new Date();
  const y = d.getFullYear().toString().slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const r = Math.floor(1000 + Math.random() * 9000);
  return `RMJ-${y}${m}${day}-${r}`;
};

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

const emptyLines = () =>
  VARIETIES.flatMap((variety) =>
    variety.skus.map((sku) => ({
      variety: variety.name,
      sku_key: sku.key,
      sku_label: sku.label,
      weight: sku.weight,
      rate: "",
      qty: "",
    }))
  );

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function OrderForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orderNo] = useState(makeOrderNo());
  const [orderDate] = useState(new Date());
  const [pricelist, setPricelist] = useState("");
  const [customer, setCustomer] = useState({ name: "", city: "" });
  const [segment, setSegment] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [fxRate, setFxRate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState(emptyLines);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [onBehalfOf, setOnBehalfOf] = useState("");
  const [salespersons, setSalespersons] = useState([]);
  const fileInputRef = useRef(null);

  const isCoordinator = user?.role === "coordinator";
  const isExportSegment = segment === "Direct Exports";
  const currencyMeta =
    CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const currencySymbol = currencyMeta.symbol;

  // Rate lock: salespersons cannot edit rates when the pricelist auto-fills
  // them (Wholesale / Retail / Bulk / Bangalore). They can still type rates
  // for Customer Specific + Export pricelists, or when no pricelist is picked.
  const rateLockedForSalesperson =
    user?.role === "salesperson" &&
    pricelist &&
    !MANUAL_PRICELISTS.has(pricelist);

  useEffect(() => {
    if (!isCoordinator) return;
    api
      .get("/salespersons")
      .then(({ data }) => setSalespersons(data || []))
      .catch(() => toast.error("Could not load salesperson list"));
  }, [isCoordinator]);

  // Auto-fill Rate column from the selected pricelist (Wholesale / Retail /
  // Bulk / Bangalore). Customer Specific & Export pricelists stay manual.
  // Direct Exports segment also stays manual since rates are in foreign currency.
  // "Loose" SKUs are ALWAYS manual (rate set per deal).
  useEffect(() => {
    if (!pricelist) return;
    if (MANUAL_PRICELISTS.has(pricelist)) return;
    if (isExportSegment) return;
    const sheet = PRICELIST_RATES[pricelist];
    if (!sheet) return;
    let filled = 0;
    setLines((prev) =>
      prev.map((l) => {
        if (l.sku_key === "loose") return l; // never overwrite/clear loose rates
        const rate = sheet[l.variety]?.[l.sku_key];
        if (rate == null) return { ...l, rate: "" };
        filled += 1;
        return { ...l, rate: String(rate) };
      })
    );
    if (filled > 0) {
      toast.success(`${pricelist}: rates auto-filled (${filled} SKUs)`);
    }
  }, [pricelist, isExportSegment]);

  const updateLine = (idx, field, value) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const { subtotal, totalQtyKg } = useMemo(() => {
    let sub = 0;
    let kg = 0;
    lines.forEach((l) => {
      const r = parseFloat(l.rate);
      const q = parseFloat(l.qty);
      if (Number.isFinite(r) && Number.isFinite(q) && q > 0) {
        sub += r * q;
        kg += q;
      }
    });
    return { subtotal: sub, totalQtyKg: kg };
  }, [lines]);

  const handleReset = () => {
    setPricelist("");
    setCustomer({ name: "", city: "" });
    setSegment("");
    setCurrency("INR");
    setFxRate("");
    setRemarks("");
    setLines(emptyLines());
    setAttachments([]);
    setOnBehalfOf("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.success("Form reset");
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const items = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 5MB — skipped`);
        continue;
      }
      const data_base64 = await readFileAsBase64(f);
      items.push({ name: f.name, size: f.size, type: f.type || "file", data_base64 });
    }
    if (items.length) {
      setAttachments((prev) => [...prev, ...items]);
      toast.success(`${items.length} file(s) attached`);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleSubmit = async () => {
    if (isCoordinator && !onBehalfOf) {
      toast.error("Select which salesperson this order is on behalf of.");
      return;
    }
    if (!customer.name.trim()) {
      toast.error("Please enter a customer name.");
      return;
    }
    if (!customer.city.trim()) {
      toast.error("Please enter the customer's city.");
      return;
    }
    if (isExportSegment && currency !== "INR") {
      const fx = parseFloat(fxRate);
      if (!Number.isFinite(fx) || fx <= 0) {
        toast.error(`Enter a valid INR conversion rate (1 ${currency} = ? ₹).`);
        return;
      }
    }
    if (totalQtyKg === 0) {
      toast.error("Add at least one quantity (Kg) before submitting.");
      return;
    }
    const conversionRate =
      currency === "INR" ? 1.0 : parseFloat(fxRate) || 0;
    const payload = {
      order_no: orderNo,
      pricelist,
      segment,
      currency,
      conversion_rate_to_inr: conversionRate,
      customer,
      remarks,
      line_items: lines
        .filter(
          (l) => parseFloat(l.rate) > 0 && parseFloat(l.qty) > 0
        )
        .map((l) => {
          const r = parseFloat(l.rate);
          const q = parseFloat(l.qty);
          const isLoose = l.sku_key === "loose";
          return {
            variety: l.variety,
            sku_key: l.sku_key,
            sku_label: l.sku_label,
            weight: l.weight,
            rate: r,
            qty: q,
            line_total: r * q,
            bags: isLoose ? null : q / l.weight,
            bag_cost: isLoose ? null : l.weight * r,
          };
        }),
      subtotal,
      subtotal_inr: subtotal * conversionRate,
      attachments,
    };
    if (isCoordinator) payload.on_behalf_of_email = onBehalfOf;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_no} submitted (Pending approval)`);
      navigate(`/orders/${data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    lines.forEach((l, idx) => {
      if (!g[l.variety]) g[l.variety] = [];
      g[l.variety].push({ ...l, idx });
    });
    return g;
  }, [lines]);

  return (
    <div className="grain ui-sans pb-16" data-testid="order-form-page">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <section className="grid md:grid-cols-3 gap-6">
          <div className="bg-white/80 backdrop-blur-sm border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="h-4 w-4 text-[#1d3a14]" />
              <h2 className="text-lg font-semibold text-[#1d3a14]">Order Info</h2>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  Order No.
                </Label>
                <Input
                  value={orderNo}
                  readOnly
                  data-testid="order-no-input"
                  className="font-mono mt-1 bg-[#faf6ea]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  Order Date
                </Label>
                <Input
                  value={format(orderDate, "dd MMM yyyy, EEEE")}
                  readOnly
                  data-testid="order-date-input"
                  className="mt-1 bg-[#faf6ea]"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  <UserCircle2 className="inline h-3 w-3 mr-1" />
                  Salesperson {isCoordinator && "*"}
                </Label>
                {isCoordinator ? (
                  <Select value={onBehalfOf} onValueChange={setOnBehalfOf}>
                    <SelectTrigger
                      data-testid="on-behalf-of-select"
                      className="mt-1 h-10"
                    >
                      <SelectValue placeholder="Order on behalf of…" />
                    </SelectTrigger>
                    <SelectContent>
                      {salespersons.map((sp) => (
                        <SelectItem
                          key={sp.email}
                          value={sp.email}
                          data-testid={`on-behalf-option-${sp.email}`}
                        >
                          <span className="font-medium">{sp.name}</span>
                          <span className="text-xs text-[#5b6b4a] ml-2">
                            {sp.email}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={user?.name || user?.email || ""}
                    readOnly
                    data-testid="salesperson-readonly"
                    className="mt-1 bg-[#faf6ea]"
                  />
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  <Tag className="inline h-3 w-3 mr-1" />
                  Pricelist
                </Label>
                <Select value={pricelist} onValueChange={setPricelist}>
                  <SelectTrigger data-testid="pricelist-select" className="mt-1 h-10">
                    <SelectValue placeholder="Select pricelist" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICELISTS.map((p) => (
                      <SelectItem
                        key={p}
                        value={p}
                        data-testid={`pricelist-option-${p.replace(/\s+/g, "-")}`}
                      >
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 bg-white/80 backdrop-blur-sm border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <User2 className="h-4 w-4 text-[#1d3a14]" />
              <h2 className="text-lg font-semibold text-[#1d3a14]">Customer Details</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  Customer Name *
                </Label>
                <Input
                  data-testid="customer-name-input"
                  value={customer.name}
                  onChange={(e) =>
                    setCustomer({ ...customer, name: e.target.value })
                  }
                  placeholder="e.g. Sri Lakshmi Traders"
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  <MapPin className="inline h-3 w-3 mr-1" />
                  City *
                </Label>
                <Input
                  data-testid="customer-city-input"
                  value={customer.city}
                  onChange={(e) =>
                    setCustomer({ ...customer, city: e.target.value })
                  }
                  placeholder="e.g. Madurai"
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  <Tag className="inline h-3 w-3 mr-1" />
                  Segment
                </Label>
                <Select value={segment} onValueChange={setSegment}>
                  <SelectTrigger
                    data-testid="segment-select"
                    className="mt-1 h-10"
                  >
                    <SelectValue placeholder="Select a segment…" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEGMENTS.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`segment-option-${s.replace(/\s+/g, "-")}`}
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isExportSegment && (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                      Currency
                    </Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger
                        data-testid="currency-select"
                        className="mt-1 h-10"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem
                            key={c.code}
                            value={c.code}
                            data-testid={`currency-option-${c.code}`}
                          >
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                      INR Conversion Rate {currency !== "INR" && "*"}
                    </Label>
                    <Input
                      data-testid="fx-rate-input"
                      type="number"
                      step="0.0001"
                      min="0"
                      disabled={currency === "INR"}
                      value={currency === "INR" ? "1" : fxRate}
                      onChange={(e) => setFxRate(e.target.value)}
                      placeholder={`1 ${currency} = ? ₹`}
                      className="mt-1"
                    />
                    <p className="text-[10px] text-[#5b6b4a] mt-1">
                      Rates &amp; totals will be captured in {currency}; INR
                      equivalent stored for reporting.
                    </p>
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  <StickyNote className="inline h-3 w-3 mr-1" />
                  Remarks
                </Label>
                <Input
                  data-testid="remarks-input"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Any special instructions"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white/85 backdrop-blur-sm border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-[#1d3a14]" />
              <h2 className="text-lg font-semibold text-[#1d3a14]">
                Products &amp; Quantities
              </h2>
            </div>
            <p className="text-xs text-[#5b6b4a]">
              {VARIETIES.length} varieties · quantities in Kg
            </p>
          </div>
          {rateLockedForSalesperson && (
            <div
              className="mb-3 text-xs bg-[#fbf8ec] border border-[#e7dfc5] rounded-md px-3 py-2 text-[#5b6b4a]"
              data-testid="rate-locked-notice"
            >
              Rate (₹/Kg) is locked because <span className="font-semibold">{pricelist}</span> rates are
              auto-applied. Use the <span className="font-semibold">Loose</span> SKU row, or pick{" "}
              <span className="font-semibold">Customer Specific Pricelist</span> if you need to enter custom rates.
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-[#e7dfc5]">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className="bg-[#1d3a14] text-[#faf6ea]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[24%]">Variety</th>
                  <th className="text-left px-3 py-2 font-medium w-[10%]">SKU</th>
                  <th className="text-right px-3 py-2 font-medium w-[10%]">Bags</th>
                  <th className="text-right px-3 py-2 font-medium w-[12%]">Qty (Kg)</th>
                  <th className="text-right px-3 py-2 font-medium w-[14%]">
                    Rate ({currencySymbol}/Kg)
                  </th>
                  <th className="text-right px-3 py-2 font-medium w-[14%]">
                    Bag Cost ({currencySymbol})
                  </th>
                  <th className="text-right px-3 py-2 font-medium w-[16%]">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(grouped).map((variety, vIdx) => {
                  const rows = grouped[variety];
                  return rows.map((row, rIdx) => {
                    const r = parseFloat(row.rate);
                    const q = parseFloat(row.qty);
                    const lineTotal =
                      Number.isFinite(r) && Number.isFinite(q) ? r * q : 0;
                    const isHas = lineTotal > 0;
                    const isLoose = row.sku_key === "loose";
                    const bagsDisplay =
                      !isLoose && Number.isFinite(q) && row.weight > 0
                        ? String(q / row.weight)
                        : "";
                    const bagCost =
                      !isLoose && Number.isFinite(r) ? row.weight * r : null;
                    const rateLocked = rateLockedForSalesperson && !isLoose;
                    return (
                      <tr
                        key={row.idx}
                        className={cn(
                          "border-t border-[#efe7cf]",
                          vIdx % 2 === 0 ? "bg-[#fbf8ec]" : "bg-white",
                          rIdx === 0 && "border-t-2 border-[#d6cdb4]"
                        )}
                      >
                        <td className="px-3 py-2 font-medium text-[#1f2a1a]">
                          {rIdx === 0 ? variety : ""}
                        </td>
                        <td className="px-3 py-2 text-[#264a1a] font-medium">
                          {row.sku_label}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {isLoose ? (
                            <span
                              className="text-[#9aa391] text-xs"
                              data-testid={`bags-na-${row.idx}`}
                            >
                              —
                            </span>
                          ) : (
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              data-testid={`bags-input-${row.idx}`}
                              value={bagsDisplay}
                              onChange={(e) => {
                                const b = parseFloat(e.target.value);
                                updateLine(
                                  row.idx,
                                  "qty",
                                  Number.isFinite(b)
                                    ? String(b * row.weight)
                                    : ""
                                );
                              }}
                              placeholder="0"
                              className="h-8 text-right bg-white"
                            />
                          )}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {isLoose ? (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              data-testid={`qty-input-${row.idx}`}
                              value={row.qty}
                              onChange={(e) =>
                                updateLine(row.idx, "qty", e.target.value)
                              }
                              placeholder="0"
                              className="h-8 text-right bg-white"
                            />
                          ) : (
                            <span
                              className={cn(
                                "font-mono tabular-nums text-sm",
                                Number.isFinite(q) && q > 0
                                  ? "text-[#1d3a14]"
                                  : "text-[#9aa391]"
                              )}
                              data-testid={`qty-display-${row.idx}`}
                            >
                              {Number.isFinite(q) && q > 0 ? q : "—"}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            data-testid={`rate-input-${row.idx}`}
                            value={row.rate}
                            readOnly={rateLocked}
                            onChange={(e) =>
                              updateLine(row.idx, "rate", e.target.value)
                            }
                            placeholder="0"
                            className={cn(
                              "h-8 text-right",
                              rateLocked
                                ? "bg-[#f0ead8] cursor-not-allowed"
                                : "bg-white"
                            )}
                            title={
                              rateLocked
                                ? "Rate is fixed by the selected pricelist"
                                : ""
                            }
                          />
                        </td>
                        <td
                          className={cn(
                            "px-3 py-2 text-right font-mono tabular-nums text-sm",
                            bagCost && bagCost > 0
                              ? "text-[#1d3a14]"
                              : "text-[#9aa391]"
                          )}
                          data-testid={`bag-cost-${row.idx}`}
                        >
                          {isLoose || !bagCost || bagCost <= 0
                            ? "—"
                            : inr(bagCost)}
                        </td>
                        <td
                          className={cn(
                            "px-3 py-2 text-right font-mono tabular-nums",
                            isHas
                              ? "text-[#1d3a14] font-semibold"
                              : "text-[#9aa391]"
                          )}
                          data-testid={`line-total-${row.idx}`}
                        >
                          {inr(lineTotal)}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white/80 backdrop-blur-sm border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-[#1d3a14]" />
              <h2 className="text-lg font-semibold text-[#1d3a14]">Upload Documents</h2>
            </div>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-button"
              className="border-[#c4b991] hover:bg-[#efe7cf]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              data-testid="file-input"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
            />
          </div>
          {attachments.length === 0 ? (
            <p className="text-sm text-[#9aa391] italic" data-testid="no-files-msg">
              No documents attached. (Optional — max 5 MB per file)
            </p>
          ) : (
            <ul className="space-y-2" data-testid="attachments-list">
              {attachments.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="flex items-center justify-between gap-3 bg-[#fbf8ec] border border-[#e7dfc5] rounded-lg px-3 py-2"
                  data-testid={`attachment-item-${idx}`}
                >
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Paperclip className="h-4 w-4 shrink-0 text-[#264a1a]" />
                    <span className="truncate font-medium text-[#1f2a1a]">{f.name}</span>
                    <span className="text-xs text-[#5b6b4a] shrink-0">
                      ({formatBytes(f.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    data-testid={`remove-attachment-${idx}`}
                    className="text-[#5b6b4a] hover:text-[#1d3a14]"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white/85 backdrop-blur-sm border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-[#1d3a14] mb-3">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5b6b4a]">Total Quantity</span>
                  <span
                    className="font-mono tabular-nums"
                    data-testid="total-qty-kg"
                  >
                    {totalQtyKg.toLocaleString("en-IN")} Kg
                  </span>
                </div>
                <Separator className="my-3 bg-[#e7dfc5]" />
                <p className="text-xs text-[#5b6b4a]">
                  Rates and quantities are entered per Kilogram. The grand total
                  is the sum of all line totals.
                </p>
              </div>
            </div>
            <div className="bg-[#1d3a14] text-[#faf6ea] rounded-xl p-5 shadow-md">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="opacity-80">Subtotal</span>
                  <span
                    className="font-mono tabular-nums"
                    data-testid="subtotal-amount"
                  >
                    {currencySymbol}
                    {subtotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                </div>
                {isExportSegment && currency !== "INR" && parseFloat(fxRate) > 0 && (
                  <div className="flex justify-between text-xs opacity-80">
                    <span>≈ INR equivalent</span>
                    <span
                      className="font-mono tabular-nums"
                      data-testid="subtotal-inr"
                    >
                      ₹{(subtotal * parseFloat(fxRate)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <Separator className="my-3 bg-[#faf6ea]/20" />
                <div className="flex justify-between items-baseline">
                  <span className="brand-serif text-xl">Grand Total</span>
                  <span
                    className="brand-serif text-3xl font-bold tabular-nums"
                    data-testid="grand-total"
                  >
                    {currencySymbol}
                    {subtotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="flex flex-wrap gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleReset}
            data-testid="reset-button"
            className="border-[#c4b991] hover:bg-[#efe7cf]"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Form
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-button"
            className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Submitting…" : "Submit Order"}
          </Button>
        </section>
      </main>
    </div>
  );
}
